import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import { ProductInfo } from "@insite/client-framework/Common/ProductInfo";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import clearProduct from "@insite/client-framework/Store/Components/ProductSelector/Handlers/ClearProduct";
import reset from "@insite/client-framework/Store/Components/ProductSelector/Handlers/Reset";
import searchProducts from "@insite/client-framework/Store/Components/ProductSelector/Handlers/SearchProducts";
import setProduct from "@insite/client-framework/Store/Components/ProductSelector/Handlers/SetProduct";
import setUnitOfMeasure from "@insite/client-framework/Store/Components/ProductSelector/Handlers/SetUnitOfMeasure";
import { getProductSelector } from "@insite/client-framework/Store/Components/ProductSelector/ProductSelectorSelectors";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getProductState } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import translate from "@insite/client-framework/Translate";
import { CartLineCollectionModel, CartLineModel, ProductModel } from "@insite/client-framework/Types/ApiModels";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import DynamicDropdown, { DynamicDropdownPresentationProps, OptionObject } from "@insite/mobius/DynamicDropdown";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Icon, { IconProps } from "@insite/mobius/Icon";
import X from "@insite/mobius/Icons/X";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import debounce from "lodash/debounce";
import React, { ChangeEvent, ReactNode, useContext, useEffect, useRef, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";
import ProductDetailsSalesRepModel from "@dana/Components/ProductDetails/ProductDetailsSalesRepModel";
import { getDanaGeneralSettings } from "@dana/Store/Context/ContextSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import addCartLines from "@insite/client-framework/Store/Data/Carts/Handlers/AddCartLines";
import ProductAddedToCartMessage from "@insite/content-library/Components/ProductAddedToCartMessage";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Clickable, { ClickableButtonProps } from "@insite/mobius/Clickable";
import { themeFontWeights } from "@dana/MobiusOverrides/styles/variables";
import { fontFamilies, fontSizes, fontWeights, lineHeights } from "@dana/MobiusOverrides/styles/typography";
import { themeBackgrounds, themeColors } from "@dana/MobiusOverrides/styles/colors";
import { HasHistory, useHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import LoadingSpinner from "@insite/mobius/LoadingSpinner";
import LoadingOverlay from "@insite/mobius/LoadingOverlay";
import loadCurrentCart from "@insite/client-framework/Store/Data/Carts/Handlers/LoadCurrentCart";
import { API_URL_CURRENT_FRAGMENT } from "@insite/client-framework/Services/ApiService";
import { addDanaLineCollection } from "@dana/Overrides/Services/CartService";

const INITIAL_PART_COUNT = 10;

interface PartEntry {
    searchTerm: string;
    qty: number;
    options: OptionObject[];
    selectedProductId?: string;
    selectedProduct?: ProductModel;
    selectedProductInfo?: ProductInfo;
    isSearching: boolean;
    errorMessage: ReactNode;
}

interface OwnProps {
    onSelectProduct: (productInfo: ProductInfo, product: ProductModel) => void;
    skipInventoryValidation?: boolean;
    denyFixedConfiguration?: boolean;
    selectButtonTitle?: string;
    productIsConfigurableMessage?: ReactNode;
    productIsUnavailableMessage?: ReactNode;
    customErrorMessage?: ReactNode;
    extendedStyles?: QuickOrderProductSelectorStyles;
}

const mapStateToProps = (state: ApplicationState) => {
    const { isSearching, searchResults, selectedProductInfo, errorType } = getProductSelector(state);
    const product = getProductState(state, selectedProductInfo?.productId).value;
    const settingsCollection = getSettingsCollection(state);
    return {
        isSearching,
        searchResults,
        selectedProductInfo,
        product,
        errorType,
        location: getLocation(state),
        danaGeneralSettings: getDanaGeneralSettings(state),
        cartPageLink: getPageLinkByPageType(state, "CartPage"),
        showAddToCartConfirmationDialog: settingsCollection.productSettings.showAddToCartConfirmationDialog,
    };
};

const mapDispatchToProps = {
    searchProducts,
    setProduct,
    clearProduct,
    setUnitOfMeasure,
    reset,
    loadCurrentCart,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface QuickOrderProductSelectorStyles {
    wrapper?: InjectableCss;
    container?: GridContainerProps;
    columnGridItem?: GridItemProps;
    innerGridContainer?: GridContainerProps;
    searchGridItem?: GridItemProps;
    qtyGridItem?: GridItemProps;
    qtyWrapper?: InjectableCss;
    minusButton?: ClickableButtonProps;
    plusButton?: ClickableButtonProps;
    qtyTextField?: TextFieldPresentationProps;
    searchDynamicDropdown?: DynamicDropdownPresentationProps;
    clearIcon?: IconProps;
    optionWrapper?: InjectableCss;
    imageWrapper?: InjectableCss;
    infoWrapper?: InjectableCss;
    autocompleteTitleText?: TypographyPresentationProps;
    autocompleteErpText?: TypographyPresentationProps;
    actionBarWrapper?: InjectableCss;
    selectButton?: ButtonPresentationProps;
    centeringWrapper?: InjectableCss;
    rowNumberText?: TypographyPresentationProps;
}

export const productSelectorStyles: QuickOrderProductSelectorStyles = {
    wrapper: {
        css: css`
            width: 100%;
        `
    },
    container: {
        gap: 24,
    },
    columnGridItem: {
        width: 12,
        css: css`
            flex-direction: column;
            gap: 24px;

             ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        null,
                        css`
                        display: grid;
                        grid-template-columns: repeat(2, 1fr);
                        grid-template-rows: repeat(5, auto);
                        grid-auto-flow: column;
                        gap: 32px 80px;
                    `,
                    ],
                    "min",
                )}
        `
    },
        rowNumberText: {
        weight: 600,
        css: css`
            position: absolute;
            left: 0;
            top: 42px;
            width: 24px;
            text-align: center;
            color: ${themeColors.primary.richBlack};
        `,
    },
    innerGridContainer: {
        gap: 16,
        css: css`
            position: relative;
            padding-left: 32px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        null,
                        css`
                       &:not(:nth-child(1)):not(:nth-child(6)) {
                            label {
                                display: none;
                            }
                                
                            [class*="IconWrapper"].clearIcon {
                                top: 20px;
                            }
                                                            [data-test-selector^="productSelector_rowNumber"] {
                                top: 12px;
                            }
                        }
                    `,
                    ],
                    "min",
                )}
        `
    },
    searchGridItem: {
        width: 9,
        css: css`
            position: relative;
            &[class*="GridItem"] {
                max-width: calc(100% - 150px);
                flex-basis: calc(100% - 150px);
            } 
            > span[class*="IconWrapper"] {
                top: 42px;
            }
        `
    },
    searchDynamicDropdown: {
        mergeCss: true,
        cssOverrides: {
            formField: css`
                [class*="PopoverStyle"] {
                    top: 42px;
                }
                .info-message-box {
                    &,
                    > span[class*="IconWrapper"] { 
                        color: ${themeColors.danger};
                    }
                }

                b {
                    font-weight: ${fontWeights.regular};
                }
                ul {
                    > div:not(:last-child) {
                        border-bottom: 1px solid ${themeBackgrounds.lightGray}; 
                    }
                }
                [class*="FormInputWrapper"] {
                    > [class*="DescriptionWrapper"] {
                        margin-top: 4px; 
                        min-width: calc(100% + 150px);
                        [data-test-selector*="productSelector_search"] {
                            font-size: ${fontSizes.small};
                        }
                    }
                    [class*="OverflowWrapper"] {
                        > [class*="IconWrapper"] {
                            display: none;
                        }
                        input:not(.react-datetime-picker__inputGroup__input) {
                            padding-right: 16px;
                            &:not(:disabled) {
                                padding: 8px 16px;
                                border-radius: 4px;
                                border-width: 1px;
                            }
                        }
                    }
                }
            `,
        }
    },
    clearIcon: {
        css: css`
            position: absolute;
            right: 16px;
            cursor: pointer;
            svg {
                width: 20px;
                height: 20px;
            }
        `,
    },
    qtyGridItem: {
        width: 3,
        css: css`
            flex-direction: column;
            &[class*="GridItem"] {
                max-width: 150px;
                flex-basis: 150px;
            }
            label {
                font: ${themeFontWeights.semiBold} ${fontSizes.small}/${lineHeights.small} ${fontFamilies.inter};
            }
            button {
                width: 30px;
                min-width: 30px;
                height: 40px;
                border: none;
                background: 0 0;
                font-size: ${fontSizes.xxLarge};
                outline: 0;
                padding: 0;
                justify-content: center;
                &:focus {
                    outline: none;
                }
            }
        `
    },
    qtyWrapper: {
        css: css`
            background: ${themeBackgrounds.lightGray};
            border: 1px solid ${themeColors.primary.richBlack};
            border-radius: 4px;
            display: inline-flex;
            margin-top: 5px; 
        `
    },
    centeringWrapper: {
        css: css`
                display: flex;
                flex-direction: column;
                height: 300px;
                justify-content: center;
                align-items: center;
            `,
    },
    qtyTextField: {
        cssOverrides: {
            formInputWrapper: css`
                input[type="number"]::-webkit-outer-spin-button,
                input[type="number"]::-webkit-inner-spin-button {
                    /* ISC-11023 deal with this. */
                    /* stylelint-disable-next-line */
                    -webkit-appearance: none;
                    margin: 0;
                }
                
            `,
            inputSelect: css`
                /* ISC-11023 deal with this. */
                /* stylelint-disable-next-line */
                -moz-appearance: textfield;
            `,
            formField: css`
                [class*="FormInputWrapper"] {
                    input:not(.react-datetime-picker__inputGroup__input) {
                        height: 40px;
                        border-radius: 0;
                        border: none;
                        padding: 0;
                        outline: 0;
                        text-align: center;   
                        border-left: 1px solid ${themeColors.primary.richBlack};
                        border-right: 1px solid ${themeColors.primary.richBlack};
                    }
                }
            `
        }
    },
    optionWrapper: {
        css: css`
            display: flex;
            cursor: pointer;
        `,
    },
    imageWrapper: {
        css: css`
            display: flex;
            width: 50px;
            height: 50px;
            margin-right: 10px;
        `,
    },
    infoWrapper: {
        css: css`
            display: flex;
        `,
    },
    autocompleteTitleText: {
        size: 14,
        css: css`
            float: left;
            padding: 12px;
        `
    },
    autocompleteErpText: {
        size: 14,
        css: css`
            padding: 12px;
        `,
    },
    actionBarWrapper: {
        css: css`
            display: flex;
            align-items: center;
            gap: 16px;
            margin-top: 36px;
            width: 100%;
        `,
    },
    selectButton: {
        css: css`
            font: ${fontWeights.medium} ${fontSizes.medium}/${lineHeights.medium} ${fontFamilies.inter};
            display: flex;
            gap: 8px;
            align-items: center;
            min-height: 42px;
            svg {
                width: 16px;
                height: 16px;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                        width: 100%;
                    `,
                    ],
                    "max",
                )}
        `,
    },
};

function makeEmptyEntries(count: number): PartEntry[] {
    return Array.from({ length: count }, () => ({
        searchTerm: "",
        qty: 1,
        options: [],
        selectedProductId: undefined,
        isSearching: false,
        errorMessage: "",
    }));
}

const QuickOrderProductSelector = ({
    onSelectProduct,
    skipInventoryValidation,
    denyFixedConfiguration,
    selectButtonTitle,
    searchProducts,
    isSearching,
    searchResults,
    setProduct,
    clearProduct,
    setUnitOfMeasure,
    selectedProductInfo,
    product,
    errorType,
    extendedStyles,
    customErrorMessage,
    productIsConfigurableMessage,
    productIsUnavailableMessage,
    location,
    reset,
    danaGeneralSettings,
    cartPageLink,

    loadCurrentCart,
}: Props) => {
    const toasterContext = useContext(ToasterContext);
    const [entries, setEntries] = useState<PartEntry[]>(
        makeEmptyEntries(INITIAL_PART_COUNT),
    );
    const [finalProduct, setFinalProduct] = useState<ProductInfo[] | null>([]);
    const [activeIndex, setActiveIndex] = useState<number | null>(null);
    const [inProgress, setInProgress] = useState(false);

    const [styles] = useState(() => mergeToNew(productSelectorStyles, extendedStyles));
    const [showDiscontinuedLabel, setDiscontinuedLabel] = useState(danaGeneralSettings?.partsObsoleteDiscontinuedStatusCode
        ?.toLowerCase()
        .split(",")
        .map((s: any) => s.trim()));
    useEffect(() => {
        reset();
    }, [location.pathname]);


    const activeIndexRef = useRef<number | null>(null);

    // Keep ref in sync with state:
    const setActiveIndexSynced = (index: number | null) => {
        activeIndexRef.current = index;
        setActiveIndex(index);
    };
    const history = useHistory();
    useEffect(() => {
        reset();
    }, [location.pathname]);

    // When global searchResults update, apply them to the active cell
    useEffect(() => {
        if (activeIndex === null || !searchResults) return;

        const newOptions = searchResults.map(p => ({
            optionText: p.displayErpNumber as any || p.erpNumber as any,
            optionValue: p.id || undefined,
            rowChildren: (
                <StyledWrapper {...styles.optionWrapper} data-test-selector={`productSelector_search_${p.id}`}>
                    <StyledWrapper {...styles.infoWrapper}>
                        <Typography {...styles.autocompleteErpText}>
                            {p.displayErpNumber || p.erpNumber}
                        </Typography>
                        <Typography {...styles.autocompleteTitleText} data-test-selector="productSelector_ProductTitle">
                            {p.displayTitle || p.title}
                        </Typography>
                    </StyledWrapper>
                </StyledWrapper>
            ),
        }));

        setEntries(prev => {
            const updated = [...prev];
            updated[activeIndex] = { ...updated[activeIndex], options: newOptions, isSearching: false };
            return updated;
        });
    }, [searchResults, activeIndex]);

    // When a product is resolved via redux, apply it to the active cell
    useEffect(() => {
        if (activeIndexRef.current === null || !selectedProductInfo || !product) return;

        const idx = activeIndexRef.current;

        setEntries(prev => {
            const updated = [...prev];
            updated[idx] = {
                ...updated[idx],
                selectedProductId: product.id,
                selectedProduct: product,
                selectedProductInfo,
                qty: selectedProductInfo.qtyOrdered ?? updated[idx].qty,
            };
            return updated;
        });
    }, [selectedProductInfo, product]);

    // Apply error messages to active cell
    useEffect(() => {
        if (activeIndex === null) return;
        let msg: ReactNode = customErrorMessage || "";
        if (errorType === "productIsConfigurable") {
            msg = productIsConfigurableMessage || translate("Cannot select configurable products");
        } else if (errorType === "productIsUnavailable") {
            msg = productIsUnavailableMessage || translate("Product is unavailable");
        }
        setEntries(prev => {
            const updated = [...prev];
            updated[activeIndex] = { ...updated[activeIndex], errorMessage: msg };
            return updated;
        });
    }, [errorType, customErrorMessage]);

    const debouncedSearch = debounce((query: string) => {
        searchProducts({ query, relevancy: false });
    }, 300);

    const onInputChanged = (index: number, event: ChangeEvent<HTMLInputElement>) => {
        setActiveIndex(index);
        setActiveIndexSynced(index);
        const value = event.target.value;
        setEntries(prev => {
            const updated = [...prev];
            updated[index] = { ...updated[index], searchTerm: value, errorMessage: "", isSearching: true };
            return updated;
        });
        if (entries[index].selectedProductId) {
            clearProduct();
        }
        debouncedSearch(value);
    };

    const onSelectionChange = (index: number, value?: string) => {
        setActiveIndex(index);
        setActiveIndexSynced(index);
        if (!searchResults || searchResults.length === 0) return;

        const variantParentId = searchResults.find(o => o.id === value)?.styleParentId;
        if (variantParentId) {
            setProduct({ productId: variantParentId, variantId: value, validateProduct: true, skipInventoryValidation, denyFixedConfiguration });
        } else {
            setProduct({ productId: value, validateProduct: true, skipInventoryValidation, denyFixedConfiguration });
        }

        setEntries(prev => {
            const updated = [...prev];
            updated[index] = { ...updated[index], selectedProductId: value, errorMessage: "" }; // clear error here
            return updated;
        });
    };

    const updateQty = (index: number, value: string) => {
        setEntries(prev => {
            const updated = [...prev];
            const entry = updated[index];
            const step = entry.selectedProductInfo?.qtyOrdered || 1;

            let parsed = parseFloat(value);
            if (!parsed || parsed < step) {
                parsed = step;
            }

            const normalized = Math.ceil(parsed / step) * step;
            updated[index] = {
                ...entry,
                qty: normalized,
            };

            return updated;
        });
    };

    const incrementQty = (index: number) => {
        setEntries(prev => {
            const updated = [...prev];
            const step = updated[index].selectedProductInfo?.qtyOrdered || 1;
            updated[index].qty = updated[index].qty + step;

            return updated;
        });
    };

    const decrementQty = (index: number) => {
        setEntries(prev => {
            const updated = [...prev];
            const step = updated[index].selectedProductInfo?.qtyOrdered || 1;
            const newQty = updated[index].qty - step;

            updated[index].qty = newQty >= step ? newQty : step;

            return updated;
        });
    };

    const clearEntry = (index: number) => {
        clearProduct();
        setEntries(prev => {
            const updated = [...prev];
            updated[index] = { ...updated[index], searchTerm: "", qty: 1, options: [], selectedProductId: undefined, errorMessage: "" };
            return updated;
        });
        searchProducts({ query: "", relevancy: false });
    };

    const handleAddToCart = () => {
        if (inProgress) return;

        // Validate: flag entries where the user typed something but never
        // selected a product from the dropdown results.
        let hasValidationError = false;
        setEntries(prev => {
            const updated = prev.map(e => {
                if (e.searchTerm.trim() !== "" && !e.selectedProductId) {
                    hasValidationError = true;
                    return {
                        ...e,
                        errorMessage: translate("Please select a product from the list."),
                    };
                }
                return e;
            });
            return updated;
        });

        if (hasValidationError) {
            return; // stop here, let the user fix the flagged rows
        }
        setInProgress(true);

        const validEntries = entries.filter(
            e => e.selectedProductId && e.selectedProduct && !e.errorMessage,
        );

        const addedEntries: PartEntry[] = [];

        validEntries.forEach(e => {
            const entryProduct = e.selectedProduct!;
            const entryProductInfo = e.selectedProductInfo!;
            const showAddToCartSection = entryProduct.properties?.danaStatusCode;
            const isAbsolute = showAddToCartSection && showDiscontinuedLabel?.includes(showAddToCartSection?.toLowerCase());
            const allowAddToCart = entryProduct?.properties?.showRtbCodeAddToCart;

            if (isAbsolute) {
                setEntries(prev => {
                    const updated = [...prev];
                    const idx = prev.indexOf(e);
                    updated[idx] = {
                        ...updated[idx],
                        errorMessage: translate("This product is obsolete, not allowed to be added to the cart."),
                    };
                    return updated;
                });
                return;
            }

            if (allowAddToCart !== "true") {
                setEntries(prev => {
                    const updated = [...prev];
                    const idx = prev.indexOf(e);
                    updated[idx] = {
                        ...updated[idx],
                        errorMessage: entryProduct?.properties?.salesRepDetails
                            ? <ProductDetailsSalesRepModel salesRepDetails={entryProduct.properties.salesRepDetails} />
                            : translate("This product is not allowed to be added to the cart."),
                    };
                    return updated;
                });
                return;
            }

            // Only reaches here if both checks passed
            addedEntries.push(e);
        });

        if (addedEntries.length === 0) {
            setInProgress(false);
            return;
        }

        if (addedEntries.length > 0) {


            const productInfoList: ProductInfo[] = addedEntries.map(e => ({
                ...e.selectedProductInfo!,
                qtyOrdered: e.qty,
            }));
            const reversedList = [...productInfoList];

            const cartLines: CartLineModel[] = [];
            reversedList.forEach(o => {
                cartLines.push({
                    productId: o.productId,
                    qtyOrdered: o.qtyOrdered,
                    unitOfMeasure: o.unitOfMeasure,

                } as CartLineModel);
            });

            addDanaLineCollection({
                cartId: API_URL_CURRENT_FRAGMENT,
                cartLineCollection: { cartLines } as CartLineCollectionModel,
            }).then(result => {
                
                if (result) {
                    setEntries(makeEmptyEntries(INITIAL_PART_COUNT));
                    clearProduct();
                    searchProducts({ query: "", relevancy: false });
                    const isQtyAdjusted = cartLines?.some(cartLine => cartLine.isQtyAdjusted) ?? false;

                    toasterContext.addToast({
                        body: <ProductAddedToCartMessage isQtyAdjusted={isQtyAdjusted} multipleProducts={true} />,
                        messageType: "success",
                    });
                    setInProgress(false);
                    cartPageLink && history.push(cartPageLink.url);
                }

            });
        }
    };

    const hasAnySelected = entries.some(e => e.selectedProductId && !e.errorMessage);

    return (
        <>
            {inProgress && (
                <><div {...styles.centeringWrapper}>
                    <LoadingOverlay className="fullLoadingSpinner" />
                </div>
                </>

            )}
            <StyledWrapper {...styles.wrapper} data-test-selector="productSelector">
                <GridContainer {...styles.container}>
                    <GridItem {...styles.columnGridItem}>
                        {entries.map((entry, index) => {
                            const step = entry.selectedProductInfo?.qtyOrdered || 1;
                            return (
                                <GridContainer {...styles.innerGridContainer} key={index}>
                                                                        <Typography
                                        {...styles.rowNumberText}
                                        data-test-selector={`productSelector_rowNumber_${index}`}
                                    >
                                        {index + 1}
                                    </Typography>
                                    <GridItem {...styles.searchGridItem}>
                                        <DynamicDropdown
                                            {...styles.searchDynamicDropdown}
                                            label={translate("Part #")}
                                            onSelectionChange={value => onSelectionChange(index, value)}
                                            onInputChange={e => onInputChanged(index, e)}
                                            filterOption={() => true}
                                            selected={entry.selectedProductId}
                                            isLoading={entry.isSearching}
                                            options={entry.options}
                                            placeholder=""
                                            error={entry.errorMessage}
                                            data-test-selector={`productSelector_search_${index}`}
                                        />
                                        {entry.selectedProductId && (
                                            <Icon
                                                {...styles.clearIcon}
                                                src={X}
                                                onClick={() => clearEntry(index)}
                                                className="clearIcon"
                                            />

                                        )}
                                    </GridItem>
                                    <GridItem {...styles.qtyGridItem}>
                                        <label htmlFor={`product_qty${index}`}> {translate("Quantity")}</label>
                                        <StyledWrapper {...styles.qtyWrapper}>
                                            <Clickable
                                                onClick={() => decrementQty(index)}
                                                {...styles.minusButton}
                                            > -
                                            </Clickable>
                                            <TextField
                                                {...styles.qtyTextField}
                                                id={`product_qty${index}`}
                                                type="number"
                                                min={step}
                                                max={999999}
                                                step={step}
                                                value={entry.qty}
                                                onChange={e => updateQty(index, e.currentTarget.value)}
                                                data-test-selector={`productSelector_qty_${index}`}
                                            />
                                            <Clickable
                                                onClick={() => incrementQty(index)}
                                                {...styles.plusButton}
                                            > +
                                            </Clickable>
                                        </StyledWrapper>
                                    </GridItem>
                                </GridContainer>
                            );
                        })}
                    </GridItem>
                </GridContainer>

                <StyledWrapper {...styles.actionBarWrapper}>
                    <Button
                        {...styles.selectButton}
                        onClick={handleAddToCart}
                        disabled={!hasAnySelected || inProgress}
                        data-test-selector="productSelector_addToCart"
                    >
                        {inProgress ? (
                            <>
                                <LoadingSpinner /> {translate("Adding...")}
                            </>
                        ) : (
                            selectButtonTitle || translate("Add to Cart")
                        )}
                    </Button>
                </StyledWrapper>
            </StyledWrapper>
        </>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(QuickOrderProductSelector);